/**
 * Pure selection rules for the disbursement table's checkboxes.
 *
 * The firm-invoice and client-net rows have a directional dependency that mirrors
 * the backend's submit/undo ordering (sc-45998 / sc-45999):
 *
 *   SUBMIT — the client net can only be pushed after the firm invoice is paid, so
 *            its checkbox is enabled only when the firm invoice is already
 *            submitted OR is currently selected (they submit together, invoice
 *            first). Deselecting the firm invoice deselects the client net.
 *
 *   UNDO   — the firm invoice can only be reversed after the client net is
 *            reversed, so its checkbox is enabled only when the client net is
 *            already undone OR is currently selected. Deselecting the client net
 *            deselects the firm invoice.
 *
 * Lien and fee-recipient rows are independent. All functions are pure over
 * (rows, selectedKeys) so they can be unit-tested without Vue.
 */
import type {
  DisbursementRow,
  DisbursementRowRef,
} from '@/models/disbursementTypes';
import { FIRM_ROW_KEY, CLIENT_ROW_KEY } from '@/utils/disbursementHelpers';

const isSubmitted = (row?: DisbursementRow): boolean => row?.status === 'done';
const byKey = (rows: DisbursementRow[], key: string): DisbursementRow | undefined =>
  rows.find((r) => r.key === key);

/**
 * Whether a row's checkbox may be checked given the current selection.
 * Encodes the invoice ↔ client-net dependency; lien and fee-recipient rows are
 * independent.
 */
export function isRowCheckable(
  row: DisbursementRow,
  rows: DisbursementRow[],
  selected: Set<string>,
): boolean {
  if (row.key === CLIENT_ROW_KEY) {
    // Undo context: the client net is the prerequisite — freely checkable.
    if (isSubmitted(row)) return row.canUndo;
    // Submit context: needs the firm invoice paid or selected (submit together).
    const firm = byKey(rows, FIRM_ROW_KEY);
    if (!firm) return row.canSubmit;
    return isSubmitted(firm) || selected.has(FIRM_ROW_KEY);
  }
  if (row.key === FIRM_ROW_KEY) {
    // Submit context: the firm invoice is the prerequisite — freely checkable.
    // Also checkable via canUndo alone (needs-recovery: invoiced but not paid —
    // canUndo can be true here even though the row was never actually submitted).
    // The client-net-first undo ordering doesn't apply in that state: canUndo is
    // only true when the client net hasn't been pushed yet (see backend CanUndo).
    if (!isSubmitted(row)) return row.canSubmit || row.canUndo;
    if (row.canUndo) return true;
    // canUndo is false. That can mean either (a) genuinely blocked right now (sc-46091:
    // e.g. QBO Cleared/Reconciled — backend sets undoBlockedReason for this case) or
    // (b) blocked purely by the client-net-first ordering, which the backend reports
    // with no reason string. Only (b) is fixable by the user, by undoing both together —
    // (a) can never become checkable no matter what else is selected.
    if (row.undoBlockedReason) return false;
    const client = byKey(rows, CLIENT_ROW_KEY);
    if (!client) return false;
    return !isSubmitted(client) || selected.has(CLIENT_ROW_KEY);
  }
  return row.canSubmit || row.canUndo;
}

/**
 * Whether a row can take part in a bulk operation at all (drives "select all").
 * The client net counts as participating when the firm invoice can be submitted,
 * because selecting both queues them together (invoice first).
 *
 * A submitted row only counts as participating when it can actually be reversed
 * (sc-46091: canUndo is false while cleared/reconciled in QuickBooks) — being
 * submitted alone doesn't make a row actionable, or "select all" would sweep up
 * rows whose individual checkbox is correctly disabled.
 */
export function isRowActionable(
  row: DisbursementRow,
  rows: DisbursementRow[],
): boolean {
  if (row.key === CLIENT_ROW_KEY) {
    if (isSubmitted(row)) return row.canUndo;
    if (row.canSubmit) return true;
    const firm = byKey(rows, FIRM_ROW_KEY);
    return !!firm && firm.canSubmit;
  }
  if (row.key === FIRM_ROW_KEY) {
    if (!isSubmitted(row)) return row.canSubmit || row.canUndo;
    if (row.canUndo) return true;
    // canUndo is false. Same distinction as isRowCheckable: (a) genuinely blocked right now
    // (sc-46091 — backend sets undoBlockedReason) is never actionable, but (b) blocked purely
    // by the client-net-first ordering (no reason string) IS actionable as part of "select
    // all" — selecting both together is exactly how that ordering gets satisfied, mirroring
    // the tax-case flow where undo of the invoice depends on the client net being undone too.
    if (row.undoBlockedReason) return false;
    const client = byKey(rows, CLIENT_ROW_KEY);
    return !!client && client.canUndo;
  }
  return row.canSubmit || row.canUndo;
}

/** Keys selected by "select all" — every actionable row (dependencies satisfied). */
export function selectAllKeys(rows: DisbursementRow[]): string[] {
  return rows.filter((r) => isRowActionable(r, rows)).map((r) => r.key);
}

/**
 * Drop any selected key that is no longer checkable under the current selection,
 * to a fixpoint. E.g. deselecting the firm invoice cascades to deselect the
 * client net; deselecting the client net cascades to deselect the firm invoice.
 */
export function reconcileSelection(
  rows: DisbursementRow[],
  selectedKeys: string[],
): string[] {
  let keys = selectedKeys.filter((k) => rows.some((r) => r.key === k));
  // Bounded fixpoint — at most one pass per row.
  for (let i = 0; i <= rows.length; i += 1) {
    const set = new Set(keys);
    const next = keys.filter((k) => {
      const row = byKey(rows, k);
      return !!row && isRowCheckable(row, rows, set);
    });
    if (next.length === keys.length) return next;
    keys = next;
  }
  return keys;
}

/**
 * Refs for a bulk Submit over the selection. Includes the selected client net
 * when the firm invoice is also selected for submit (they go in one call,
 * invoice first — the backend enforces the order).
 */
export function bulkSubmitRefs(
  rows: DisbursementRow[],
  selectedKeys: string[],
): DisbursementRowRef[] {
  const set = new Set(selectedKeys);
  const firmSubmitting = rows.some(
    (r) => r.key === FIRM_ROW_KEY && set.has(r.key) && r.canSubmit,
  );
  return rows
    .filter(
      (r) =>
        set.has(r.key) &&
        (r.canSubmit ||
          (r.key === CLIENT_ROW_KEY && firmSubmitting && !isSubmitted(r))),
    )
    .map((r) => r.ref);
}

/**
 * Refs for a bulk Undo over the selection. Includes the selected firm invoice
 * when the client net is also selected for undo (one call, client net first).
 */
export function bulkUndoRefs(
  rows: DisbursementRow[],
  selectedKeys: string[],
): DisbursementRowRef[] {
  const set = new Set(selectedKeys);
  const clientUndoing = rows.some(
    (r) => r.key === CLIENT_ROW_KEY && set.has(r.key) && r.canUndo,
  );
  return rows
    .filter(
      (r) =>
        set.has(r.key) &&
        (r.canUndo ||
          (r.key === FIRM_ROW_KEY && clientUndoing && isSubmitted(r))),
    )
    .map((r) => r.ref);
}
